/**
 * Decoded audio for the preview players, shared between them.
 *
 * A decoded record is large (about 85 MB for four minutes of stereo), so only a
 * few are kept: enough for the record playing and the one or two after it. A
 * record still playing keeps its own buffer alive through its source node even
 * after it leaves this cache.
 */

import { audioUrl } from "../api";
import { readPref, writePref } from "./prefs";

const CACHE_SIZE = 4;
const buffers = new Map<string, Promise<AudioBuffer>>();

export function loadAudio(ctx: AudioContext, trackId: string): Promise<AudioBuffer> {
  const cached = buffers.get(trackId);
  if (cached) {
    // Most recently used goes to the back, so eviction takes the oldest.
    buffers.delete(trackId);
    buffers.set(trackId, cached);
    return cached;
  }
  const pending = fetch(audioUrl(trackId))
    .then((res) => {
      if (!res.ok) throw new Error(`the engine could not serve the audio (${res.status})`);
      return res.arrayBuffer();
    })
    .then((bytes) => ctx.decodeAudioData(bytes));
  pending.catch(() => buffers.delete(trackId));
  buffers.set(trackId, pending);
  while (buffers.size > CACHE_SIZE) buffers.delete(buffers.keys().next().value as string);
  return pending;
}

export function isLoaded(trackId: string): boolean {
  return buffers.has(trackId);
}

interface Output {
  ctx: AudioContext;
  master: GainNode;
}

const outputs = new Map<string, Output>();
/** Device chosen per output, by name; "" is the system default. */
const sinks = new Map<string, string>();

type SinkContext = AudioContext & { setSinkId?: (id: string) => Promise<void> };

/**
 * One audio context and master volume per named output: "main" for the library's
 * player, others for plugins (Radio's "onair"). Separate contexts so each can play
 * to its own device. Call from a click.
 */
export async function audioOutput(name = "main"): Promise<Output> {
  let out = outputs.get(name);
  if (!sinks.has(name)) sinks.set(name, readPref(`nova.output.${name}`, ""));
  if (!out) {
    const ctx = new AudioContext();
    const master = ctx.createGain();
    master.connect(ctx.destination);
    out = { ctx, master };
    outputs.set(name, out);
    const sink = sinks.get(name);
    if (sink) await applySink(out.ctx, sink);
  }
  // Never waits on a sound that will not wake: the player decides what to do (player.wake).
  if (out.ctx.state === "suspended") await Promise.race([out.ctx.resume().catch(() => undefined), new Promise((r) => setTimeout(r, 2000))]);
  return out;
}

/** Close an output's context so the next audioOutput() makes a fresh one (a sound that would not wake). */
export async function resetOutput(name: string): Promise<void> {
  const out = outputs.get(name);
  outputs.delete(name);
  if (out) await Promise.race([out.ctx.close().catch(() => undefined), new Promise((r) => setTimeout(r, 500))]);
}

async function applySink(ctx: AudioContext, deviceId: string): Promise<boolean> {
  const c = ctx as SinkContext;
  if (typeof c.setSinkId !== "function") return false;
  try {
    await c.setSinkId(deviceId);
    return true;
  } catch {
    return false;
  }
}

/**
 * Whether this webview can send sound to a chosen device. True on Windows
 * (WebView2) and in Chromium browsers; false in WebKitGTK (Linux) and WebKit (macOS),
 * where everything plays on the system's output.
 */
export const canChooseOutput = (): boolean =>
  typeof AudioContext !== "undefined" && typeof (AudioContext.prototype as SinkContext).setSinkId === "function";

/** Send an output to a device ("" for the system default). False when it could not. */
export async function setOutputDevice(name: string, deviceId: string): Promise<boolean> {
  sinks.set(name, deviceId);
  writePref(`nova.output.${name}`, deviceId);
  const out = outputs.get(name);
  return out ? applySink(out.ctx, deviceId) : canChooseOutput();
}

/** A media element (the tag editor's waveform) sent to the same device as an output. */
export async function routeElement(el: HTMLMediaElement, name: string): Promise<void> {
  const e = el as HTMLMediaElement & { setSinkId?: (id: string) => Promise<void> };
  const sink = sinks.get(name) ?? readPref(`nova.output.${name}`, "");
  if (sink !== undefined && typeof e.setSinkId === "function") await e.setSinkId(sink).catch(() => undefined);
}

/** The device an output is set to ("" for the system default). */
export const outputDevice = (name: string): string => sinks.get(name) ?? readPref(`nova.output.${name}`, "");

export interface OutputDevice {
  id: string;
  label: string;
}

/**
 * The sound outputs this computer has. Browsers hide their names until the page
 * has been allowed a microphone once; `askNames` asks for that.
 */
export async function outputDevices(askNames = false): Promise<{ devices: OutputDevice[]; named: boolean }> {
  const md = typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;
  if (!md?.enumerateDevices) return { devices: [], named: false };
  if (askNames) {
    try {
      const stream = await md.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
    } catch {
      // Refused: the list still works, without names.
    }
  }
  const all = (await md.enumerateDevices()).filter((d) => d.kind === "audiooutput");
  const named = all.some((d) => d.label);
  return {
    devices: all
      .filter((d) => d.deviceId !== "default" && d.deviceId !== "communications")
      .map((d, i) => ({ id: d.deviceId, label: d.label || `Output ${i + 1}` })),
    named,
  };
}
